/**
 * @fileoverview The own to-do sheets for a page (D50; E25): their state, their writes and their three ways in:
 * openAdd (the reminder sheet, OwnTodoSheet: what the to-do is about, the garden from Today, or a plant or bed from its
 * card), openEdit (a to-do's id, key, title, planned when and, carried from an earlier day, its card's "Since …"), and
 * openPlant ("Plant something", PlantSomethingSheet: from Today, or a bed's card with its bed; E25, owner, 30 Oct: two
 * buttons, two sheets). openKey is the to-do last added or edited by either sheet, for Today's groups to show and scroll
 * to. The page draws element once.
 *
 * The old way from the reminder sheet's "Plant something" switch into Add a plant (E18, E23's PlantFromTodo and
 * plantTodoKept) is gone: "Plant something" saves the plant and its to-do together on the server.
 *
 * Where: TodayPage (Today's two buttons, a card's Edit), OwnTodosRow (a plant's and a bed's card).
 * API: through useOwnTodos (POST /todo/own, PATCH /todo/own/:id), usePlantSomething (GET /todo/own/plant/plan, POST
 * /todo/own/plant and its Undo) and the page's useTodoMarks.
 *
 * @module own-todos/useOwnTodoSheet
 */

import React from 'react';
import type { TodoAsk } from '@/shared/api/todo';
import type { TodoMarks } from '@/features/today/lib/useTodoMarks';
import { useOwnTodos } from './useOwnTodos';
import { OwnTodoSheet, type Mode, type OwnAddTarget, type OwnEditTarget } from './OwnTodoSheet';
import { PlantSomethingSheet } from './PlantSomethingSheet';
import { usePlantSomething, type PlantTarget } from './usePlantSomething';

/** What useOwnTodoSheet gives a page. */
export interface OwnTodoSheetControl {
  /** Opens the reminder sheet to add a to-do about the garden (Today), a plant or a bed. */
  openAdd: (target: OwnAddTarget) => void;
  /** Opens the reminder sheet to edit a to-do. */
  openEdit: (target: OwnEditTarget) => void;
  /** Opens "Plant something", from Today (no bed) or a bed's card (its bed). */
  openPlant: (target: PlantTarget) => void;
  /** The sheets, to draw once on the page. */
  element: React.ReactNode;
  /** The key of the to-do last added or edited (its group shows it and scrolls to it); null for none. */
  openKey: string | null;
}

/** "Plant something" from Today: no bed of its own. */
const FROM_TODAY: PlantTarget = { locationId: null };

/**
 * The own to-do sheets for a page: their state, their writes, and their ways in.
 *
 * @param queryKey - The key the brief is kept under (useTodayBrief).
 * @param ask - How the brief was asked for (useTodayBrief).
 * @param marks - The page's useTodoMarks (Delete, and the toasts' Undo).
 * @param today - The garden's day, "YYYY-MM-DD" (the brief's).
 * @returns The control.
 */
export function useOwnTodoSheet(queryKey: readonly unknown[], ask: TodoAsk, marks: TodoMarks, today: string): OwnTodoSheetControl {
  const own = useOwnTodos(queryKey, ask, marks);
  const [mode, setMode] = React.useState<Mode | null>(null);
  const [open, setOpen] = React.useState(false);
  const boxRef = React.useRef<HTMLInputElement>(null);
  /** "Plant something" is open. */
  const [planting, setPlanting] = React.useState(false);
  /** Where "Plant something" was opened from. */
  const [plantTarget, setPlantTarget] = React.useState<PlantTarget>(FROM_TODAY);
  /** The to-do last added by "Plant something", or null. */
  const [plantKey, setPlantKey] = React.useState<string | null>(null);
  /** Which sheet saved last: its key is the one shown. */
  const [lastBy, setLastBy] = React.useState<'own' | 'plant'>('own');
  // The reminder sheet saved since: its key leads again.
  const [ownKeySeen, setOwnKeySeen] = React.useState(own.openKey);
  if (own.openKey !== ownKeySeen) {
    setOwnKeySeen(own.openKey);
    setLastBy('own');
  }
  const { clearFailed } = own;
  /** Opens the reminder sheet to add a to-do about the target (the garden, a plant or a bed), with no old failure showing. */
  const openAdd = React.useCallback((target: OwnAddTarget) => {
    clearFailed();
    setMode({ kind: 'add', target });
    setOpen(true);
  }, [clearFailed]);
  /** Opens the reminder sheet to edit a to-do, with no old failure showing. */
  const openEdit = React.useCallback((target: OwnEditTarget) => {
    clearFailed();
    setMode({ kind: 'edit', target });
    setOpen(true);
  }, [clearFailed]);
  /** Opens "Plant something" from its place. */
  const openPlant = React.useCallback((target: PlantTarget) => {
    setPlantTarget((current) => (current.locationId === target.locationId ? current : target));
    setPlanting(true);
  }, []);
  /**
   * "Plant something" saved: its to-do's key leads.
   *
   * @param key - The new to-do's key, or null.
   */
  const plantSaved = React.useCallback((key: string | null) => {
    setPlantKey(key);
    setLastBy('plant');
  }, []);
  const plant = usePlantSomething({ open: planting, target: plantTarget, today, ask, queryKey, onSaved: plantSaved });
  const element = (
    <>
      <OwnTodoSheet mode={mode} open={open} onOpenChange={setOpen} today={today} own={own} marks={marks} boxRef={boxRef} />
      <PlantSomethingSheet open={planting} onOpenChange={setPlanting} today={today} sheet={plant} />
    </>
  );
  return { openAdd, openEdit, openPlant, element, openKey: lastBy === 'plant' ? plantKey : own.openKey };
}
